<template>
  <model-form
    ref="modelForm"
    :model="category"
    modal-type="modal"
  >
    <q-tabs v-model="tab">
      <q-tab
        name="details"
        label="Details"
      />
      <q-tab
        name="restrictions"
        label="Restrictions"
      />
      <q-tab
        name="displayOrder"
        label="Display Order"
      />
    </q-tabs>
    <q-tab-panels v-model="tab">
      <q-tab-panel name="details">
        <div class="row q-col-gutter-sm items-start">
          <model-form-input
            field="name"
            class="col-6"
            label="Name"
            :rules="[val => !!val || 'Field is required']"
            autofocus
          />
          <div class="col-6 q-mb-md">
            <model-form-image-uploader
              field="image"
              :image-url="category.imageUrl"
            />
          </div>
          <div
            class="row q-mb-lg q-col-gutter-sm items-start"
          >
            <model-form-checkbox
              field="availableOnline"
              class="col"
              label="Available Online"
            >
              <q-tooltip>
                Display and sell items in this category on your website.
              </q-tooltip>
            </model-form-checkbox>
            <model-form-checkbox
              field="hasAlcoholAcknowledgement"
              class="col"
              label="Display Alcohol Acknowledgement"
            >
              <q-tooltip>
                Display a notification to users when they purchase an item from this category online, indicating that the item contains alcohol.
              </q-tooltip>
            </model-form-checkbox>
          </div>
        </div>
      </q-tab-panel>
      <q-tab-panel name="restrictions">
        <div
          class="row q-col-gutter-x-sm items-center"
        >
          <model-form-select
            field="conditions"
            class="col-12 q-mt-md"
            label="Conditions"
            :options="$mh.asOptions(CONDITIONS.ALL)"
            map-options
            emit-value
            multiple
            use-chips
            clearable
            :decode="conditions => $mh.objectToArray(conditions)"
            :encode="enabledConditions => $mh.arrayToObject(enabledConditions || [], { allKeys: CONDITIONS.ALL })"
          >
            <template #option="scope">
              <q-item
                v-bind="scope.itemProps"
                v-on="scope.itemEvents"
              >
                <q-item-section>
                  <div class="row items-center justify-between q-gutter-x-sm">
                    <span>{{ scope.opt.label }}</span>
                    <q-icon
                      name="info"
                      size="sm"
                    >
                      <q-tooltip>
                        {{ CONDITIONS_TOOLTIPS[scope.opt.value] }}
                      </q-tooltip>
                    </q-icon>
                  </div>
                </q-item-section>
              </q-item>
            </template>
            <q-tooltip>
              Set conditions to be met for members to earn these points. If multiple conditions are selected, all must be met to earn points.
            </q-tooltip>
          </model-form-select>
        </div>
        <date-range-condition-form
          v-if="category.conditions[CONDITIONS.DATE_RANGE]"
          :conditional="category"
        />
        <div
          v-if="category.conditions[CONDITIONS.DELIVERY_TYPES]"
          class="row q-col-gutter-x-sm items-center"
        >
          <q-select
            v-model="category.conditionsRequirements[CONDITIONS_REQUIREMENTS.DELIVERY_TYPES]"
            class="col-8"
            :label="`${(category.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_DELIVERY_TYPES] ? 'Exclude' : 'Include')} Delivery Types`"
            :options="$mh.asOptions(['theater-seat', 'table', 'counter-pickup'])"
            map-options
            emit-value
            multiple
            use-chips
            :rules="[
              val => !!val || 'Field is required',
              val => val.length > 0 || 'Must select at least one delivery type'
            ]"
          />
          <q-toggle
            v-model="category.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_DELIVERY_TYPES]"
            class="col-4 q-mb-md"
            label="Exclude"
          />
        </div>
        <membership-types-condition-form
          v-if="category.conditions[CONDITIONS.MEMBERSHIP_TYPES]"
          :conditional="category"
        />
        <membership-tiers-condition-form
          v-if="category.conditions[CONDITIONS.MEMBERSHIP_TIERS]"
          :conditional="category"
        />
        <showing-badges-condition-form
          v-if="category.conditions[CONDITIONS.SHOWING_BADGES]"
          :conditional="category"
          :match-showing-badges-field="CONDITIONS_REQUIREMENTS.MATCH_SHOWING_BADGES"
        />
        <time-of-day-condition-form
          v-if="category.conditions[CONDITIONS.TIME_OF_DAY]"
          :conditional="category"
        />
      </q-tab-panel>
      <q-tab-panel name="displayOrder">
        <set-display-order-form
          :list="activeItems"
          :loading="reordering"
          @reorder="handleReorder"
        />
      </q-tab-panel>
    </q-tab-panels>
  </model-form>
</template>

<script>
import { CONDITIONS, CONDITIONS_REQUIREMENTS } from 'src/models/item/Category'
import Model from 'src/models/model'
import DateRangeConditionForm from 'src/modules/admin/components/condition-requirement-forms/DateRangeConditionForm'
import MembershipTiersConditionForm from 'src/modules/admin/components/condition-requirement-forms/MembershipTiersConditionForm'
import MembershipTypesConditionForm from 'src/modules/admin/components/condition-requirement-forms/MembershipTypesConditionForm'
import ShowingBadgesConditionForm from 'src/modules/admin/components/condition-requirement-forms/ShowingBadgesConditionForm'
import TimeOfDayConditionForm from 'src/modules/admin/components/condition-requirement-forms/TimeOfDayConditionForm'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import SetDisplayOrderForm from 'src/modules/admin/components/SetDisplayOrderForm'

const CONDITIONS_TOOLTIPS = {
  [CONDITIONS.DATE_RANGE]: 'Category will be displayed online for showings during a given date range.',
  [CONDITIONS.DELIVERY_TYPES]: 'Category will be displayed online for orders allowing given delivery types.',
  [CONDITIONS.MEMBERSHIP_TYPES]: 'Category will be displayed online for members matching the given membership type.',
  [CONDITIONS.MEMBERSHIP_TIERS]: 'Category will be displayed online for members matching the given membership tier.',
  [CONDITIONS.SHOWING_BADGES]: 'Category will be displayed online for showings with given showing badges.',
  [CONDITIONS.TIME_OF_DAY]: 'Category will be displayed online for showings during a given time range.'
}

export default {
  name: 'CategoryForm',
  components: {
    ModelForm,
    DateRangeConditionForm,
    MembershipTypesConditionForm,
    MembershipTiersConditionForm,
    ShowingBadgesConditionForm,
    TimeOfDayConditionForm,
    SetDisplayOrderForm
  },
  configuration: ['showingBadges'],
  models: {
    category: {
      membershipTypes: {
        fields: ['id', 'name']
      },
      membershipTiers: {
        fields: ['id', 'name'],
        membershipType: {
          fields: ['id', 'name']
        }
      },
      showingBadges: {
        fields: ['id', 'name']
      },
      libraryChildSiteIds: true,
      items: true
    }
  },
  data () {
    return {
      tab: 'details',
      reordering: false
    }
  },
  computed: {
    orderedItems () {
      return _.orderBy(this.category.items.data, 'displayOrder')
    },
    activeItems () {
      return _.filter(this.orderedItems, 'isUnarchived')
    },
    archivedItems () {
      return _.filter(this.orderedItems, 'isArchived')
    }
  },
  created () {
    _.extend(this, { CONDITIONS, CONDITIONS_REQUIREMENTS, CONDITIONS_TOOLTIPS })
  },
  methods: {
    async handleReorder (reorderedList) {
      this.reordering = true

      this.category.items.data = _(reorderedList).concat(this.archivedItems).map((item, index) => {
        item.displayOrder = index + 1
        return item
      }).value()

      // Save all models
      await Model.configuration.batchSave(async () => {
        const saveMutations = _(this.category.items.data).filter({ isDirty: true }).map(model => model.save()).value()
        await Promise.all(saveMutations)
      })

      await Model.configuration.validate()

      this.reordering = false
    }
  }
}

</script>
